import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import * as Yup from 'yup'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {
  OPTIONS_CONTENT_TYPE,
  OPTIONS_CONTENT_TYPE_FEDEX,
  OrdersConfig,
  OrderStatus,
} from '../core/_const'
import {
  ACCEPT_TERM_UPIC_KEY,
  DIMENSION_UNIT,
  FEDEX_INTERNATIONAL_SERVICES,
  REGEX_ALPHANUMERIC,
  SERVICES,
  WEIGHT_UNIT,
} from '../../../../_gori/constants'
import {cloneDeep, isEmpty, last, omit} from 'lodash'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import OrderService from '../core/_requests'
import {convertUserTimeZone, StorageHelpers} from '../../../../_gori/helpers'
import {useAuth} from '../../auth'
import {DEFAULT_SETTINGS} from '../../common/core/_const'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {getErrorPaths, getKeyOrder} from '../../../../_gori/utils'
import Cookies from 'js-cookie'

type UseOrderChildProps = {
  dataCopy?: any
  id?: number | string
  setLoading: (loading: any) => void
  onSubmit: (values: any) => void
}
export const useOrderChild = ({dataCopy, id, setLoading, onSubmit}: UseOrderChildProps) => {
  const row = useRef({parcels: 0, order_items: 0, custom_items: 0})
  const {currentUser} = useAuth()
  const {newCancelToken, isCancel} = useCancelToken()
  const intl = useIntl()

  const [data, setData] = useState<any>(null)
  const [currentDimensionUnit, setCurrentDimensionUnit] = useState<string>(DIMENSION_UNIT.IN)
  const [currentWeightUnit, setCurrentWeightUnit] = useState<string>(WEIGHT_UNIT.OZ)
  const [scrollToErrors, setScrollToErrors] = useState<boolean>(false)

  const {stringYup, orderYup, regexYup, numberYup, infoYup} = UseYupValidate()

  const [isAccept, setIsAccept] = useState<boolean>(false)
  const [isErrorUpicAccept, setIsErrorUpicAccept] = useState<boolean>(false)

  useEffect(() => {
    const uPicAccepted = Cookies.get(ACCEPT_TERM_UPIC_KEY)
    setIsAccept(uPicAccepted === 'true')
  }, [isAccept])

  const validateItems = useMemo(() => {
    return Yup.object().shape({
      title: stringYup(255, 'NAME'),
      sku: stringYup(70, 'SKU', false),
      quantity: numberYup.quantity('QUANTITY', false),
      unit_price: numberYup.unitPrice('PRICE', false),
    })
  }, [numberYup, stringYup])

  const validateParcels = useMemo(() => {
    return Yup.object().shape({
      length: numberYup.parcel('length'),
      width: numberYup.parcel('width'),
      height: numberYup.parcel('height'),
      weight: numberYup.parcel('weight'),
      dimension_unit: stringYup(50, 'UNIT'),
      weight_unit: stringYup(50, 'UNIT'),
      package_count: numberYup.parcel('package_count'),
    })
  }, [numberYup, stringYup])

  const validateCustoms = useCallback(
    (name) => {
      const customsValidation = {
        content: orderYup.customs.content,
        content_description: orderYup.customs.content_description,
        description: orderYup.customs.description,
        hs_tariff_number: orderYup.customs.hsTariffNumber,
        country: orderYup.customs.country,
        quantity: orderYup.customs.quantity,
        value: orderYup.customs.value,
        weight: orderYup.customs.weight,
      }

      return customsValidation[name] || Yup.string().trim()
    },
    [orderYup.customs]
  )
  const validateAddress = useCallback(
    (name) => {
      const validate = {
        [`${name}_company`]: Yup.string().when([`${name}_first_name`], {
          is: (first_name) => !first_name || first_name.trim() === '',
          then: orderYup.companyFirstName('COMPANY'),
          otherwise: stringYup(255, 'COMPANY', false),
        }),
        [`${name}_first_name`]: Yup.string().when(`${name}_company`, {
          is: (company) => !company || company.trim() === '',
          then: orderYup.companyFirstName('FIRST_NAME'),
          otherwise: stringYup(255, 'FIRST_NAME', false),
        }),
        [`${name}_street1`]: stringYup(255, 'ADDRESS'),
        [`${name}_city`]: stringYup(255, 'CITY'),
        [`${name}_state`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.state(),
          otherwise: stringYup(255, 'STATE'),
        }),
        [`${name}_country`]: regexYup.country(),
        [`${name}_zip`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.zipCode(),
          otherwise: stringYup(255, 'POSTAL_CODE'),
        }),
        [`${name}_last_name`]: stringYup(255, 'LAST_NAME', false),
        [`${name}_street2`]: stringYup(255, 'ADDRESS_2', false),
        [`${name}_phone`]: infoYup.phone('PHONE', false),
        [`${name}_email`]: infoYup.email('EMAIL', false),
      }
      return validate
    },
    [orderYup, regexYup, stringYup, infoYup]
  )

  const initValidateSchema = useMemo(() => {
    return {
      from: Yup.object().shape(
        Object.entries(validateAddress('from')).reduce((validate, [key, value]) => {
          validate[key] = value
          return validate
        }, {}),
        [['from_first_name', 'from_company']]
      ),
      to: Yup.object().shape(
        Object.entries(validateAddress('to')).reduce((validate, [key, value]) => {
          validate[key] = value
          return validate
        }, {}),
        [['to_first_name', 'to_company']]
      ),
      parcels: Yup.object().shape({
        [row.current.parcels]: Yup.object().shape(
          OrdersConfig.PARCEL.reduce((initParcel, currentName) => {
            switch (currentName) {
              case 'length':
              case 'width':
              case 'height':
              case 'weight':
              case 'package_count':
                initParcel[currentName] = numberYup.parcel(currentName)
                break
              default:
                initParcel[currentName] = stringYup(50, 'UNIT')
                break
            }
            return initParcel
          }, {})
        ),
      }),
      ship_by_date: orderYup.shipByDate,
      international: Yup.boolean(),
      international_option: Yup.object().when('international', {
        is: (val) => val === true,
        then: Yup.object().shape({
          content: validateCustoms('content'),
          description: validateCustoms('content_description'),
        }),
      }),
      custom_items: Yup.object().when('international', {
        is: (val) => val === true,
        then: Yup.object().shape({
          [row.current.custom_items]: Yup.object().shape(
            Object.entries(OrdersConfig.CUSTOMS.ITEMS).reduce((initCustoms, [key, value]) => {
              initCustoms[value.value] = validateCustoms(value.value)
              return initCustoms
            }, {})
          ),
        }),
      }),
      check_weight: Yup.boolean().when('international', {
        is: (val) => val === true,
        then: orderYup.checkIntlWeight,
      }),
      check_package_count: orderYup.checkPackageCount,
      service: stringYup(50, 'SERVICE', false),
      note: stringYup(50, 'NOTE', false),
      insurance_option: Yup.object().shape({
        insured_amount: orderYup.insuredAmount,
      }),
      reference1: orderYup.reference('REF_1'),
      reference2: orderYup.reference('REF_2'),
      reference3: orderYup.reference('REF_3'),

      claim_category: stringYup(255, 'MERCHANDISE_CATEGORY', false),
    }
  }, [numberYup, orderYup, stringYup, validateAddress, validateCustoms])

  const [validateSchema, setValidateSchema] = useState<any>(initValidateSchema)

  const initValueEdit = useCallback(
    (name, init) => {
      let _data = cloneDeep(data || dataCopy)
      if (_data?.international_option?.custom_items) {
        _data.custom_items = dataCopy
          ? _data?.international_option?.custom_items.map((item) =>
              omit(item, ['id', 'international_option_id', 'order_item_id'])
            )
          : _data?.international_option?.custom_items
      }

      if ((data || dataCopy) && _data?.[name]?.length >= 1) {
        let _valueName: any = {}
        let _validateName: any = {}

        for (let i = 0; i < _data?.[name]?.length; i++) {
          _valueName[row.current[name]] = _data?.[name][i]
          switch (name) {
            case 'order_items':
              _validateName[row.current[name]] = validateItems
              break
            case 'custom_items':
              _validateName[row.current[name]] = Yup.object()
                .default({})
                .shape(
                  Object.entries(OrdersConfig.CUSTOMS.ITEMS).reduce((initCustoms, [key, value]) => {
                    initCustoms[value.value] = validateCustoms(value.value)
                    return initCustoms
                  }, {})
                )
              break
            default:
              _validateName[row.current[name]] = validateSchema[name]?.fields?.[0]
              break
          }

          if (i < _data?.[name]?.length - 1) {
            row.current[name] += 1
          }
        }

        init[name] = _valueName

        switch (name) {
          case 'custom_items':
            setValidateSchema((prev) => {
              return {
                ...prev,
                [name]: Yup.object().when('international', {
                  is: (val) => val === true,
                  then: Yup.object().shape({..._validateName}),
                }),
              }
            })
            break
          default:
            setValidateSchema((prev) => {
              return {
                ...prev,
                [name]: Yup.object().shape({..._validateName}),
              }
            })
            break
        }
      } else {
        switch (name) {
          case 'parcels':
            init[name] = {
              0: {
                package_id: null,
                package_type: '',
                length: null,
                width: null,
                height: null,
                weight: null,
                dimension_unit: DIMENSION_UNIT.IN,
                weight_unit: WEIGHT_UNIT.OZ,
                package_count: 1,
              },
            }
            break
          case 'custom_items':
            init[name] = {
              0: {
                description: '',
                hs_tariff_number: '',
                country: '',
                quantity: '',
                value: '',
                weight: '',
              },
            }
            break

          default:
            break
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, dataCopy]
  )

  const validationSchema = Yup.object().shape(validateSchema, [['international', 'custom_items']])

  const getInitValue = useCallback(async () => {
    if (!id) return
    setLoading((prev) => ({...prev, first: true}))
    try {
      const res = await OrderService.get(id, {cancelToken: newCancelToken()})
      if (res) {
        const date = convertUserTimeZone(res.order?.ship_by_date, currentUser, '')
        setData({...res.order, ship_by_date: date})
      }
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, first: false}))
    }

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken, id])

  useEffect(() => {
    if (!id) return
    getInitValue()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInitValue, id])
  // END: initialize to initial value

  const initialValues = useMemo(() => {
    const tempOrder = StorageHelpers.getItemLocalStorage('TEMPORARY_ORDER')
    if (tempOrder) {
      StorageHelpers.removeItemLocalStorage('TEMPORARY_ORDER')
      return tempOrder
    }

    const targetData = data || dataCopy
    let insuranceOption = {
      insured_amount: targetData?.insurance_option?.insured_amount?.toString() || null,
      provider: targetData?.insurance_option?.provider || null,
      insurance_type: targetData?.insurance_option?.insurance_type || null,
      insured_fee: targetData?.insurance_option?.insured_fee || null,
    }

    if (targetData?.status === OrderStatus.SHIPPED) {
      insuranceOption = {
        insured_amount:
          targetData?.latest_shipment?.insurance_option?.insured_amount?.toString() || null,
        provider: targetData?.latest_shipment?.insurance_option?.provider || null,
        insurance_type: targetData?.latest_shipment?.insurance_option?.insurance_type || null,
        insured_fee: targetData?.latest_shipment?.insurance_option?.insured_fee || null,
      }
    }

    const init: any = Object.entries(OrdersConfig.SHIPPING).reduce(
      (initValue, [_, value]) => {
        switch (value.value) {
          case OrdersConfig.SHIPPING.RESIDENTIAL.value:
            initValue.from[`from_${value.value}`] = targetData?.[`from_${value.value}`] || false
            initValue.to[`to_${value.value}`] = targetData?.[`to_${value.value}`] ?? true
            break
          case OrdersConfig.SHIPPING.VERIFIED.value:
          case OrdersConfig.SHIPPING.SAVE_ADDRESS.value:
            break
          case OrdersConfig.SHIPPING.COUNTRY.value:
            initValue.from[`from_${value.value}`] =
              targetData?.[`from_${value.value}`] || DEFAULT_SETTINGS.COUNTRY
            initValue.to[`to_${value.value}`] =
              targetData?.[`to_${value.value}`] || DEFAULT_SETTINGS.COUNTRY
            break
          default:
            initValue.from[`from_${value.value}`] = targetData?.[`from_${value.value}`] || ''
            initValue.to[`to_${value.value}`] = targetData?.[`to_${value.value}`] || ''
            break
        }
        return initValue
      },
      {from: {}, to: {}}
    )
    initValueEdit('parcels', init)
    init.ship_by_date =
      isEmpty(targetData?.ship_by_date) || targetData?.ship_by_date === 'Invalid date'
        ? undefined
        : targetData?.ship_by_date
    init.international = !!targetData?.international_option
    init.international_option = {
      content: targetData?.international_option?.content || null,
      description: targetData?.international_option?.description || null,
    }
    initValueEdit('custom_items', init)
    initValueEdit('order_items', init)
    init.estimated_rate = targetData?.estimated_rate || null
    init.is_cheapest_rate = targetData?.is_cheapest_rate || false
    init.delivery_option_id = targetData?.delivery_option_id || null
    const latestShipment: any = last(targetData?.shipments) || null
    init.service =
      targetData?.status === OrderStatus.SHIPPED
        ? latestShipment?.carrier && latestShipment?.service
          ? `${latestShipment?.carrier}_${latestShipment?.service}`
          : null
        : targetData?.carrier && targetData?.service
        ? `${targetData?.carrier}_${targetData?.service}`
        : null
    init.signature_confirmation = targetData?.signature_confirmation || null
    init.extra_service = targetData?.extra_service?.code
      ? targetData?.extra_service?.code?.toString()
      : null
    init.insurance_option = insuranceOption
    init.preset_id = targetData?.preset_id || null
    init.check_weight = false
    init.check_package_count = false
    init.reference1 = targetData?.reference1 || null
    init.reference2 = targetData?.reference2 || null
    init.reference3 = targetData?.reference3 || null
    init.customer_tracking_number = latestShipment?.customer_tracking_number || ''

    init.tags = targetData?.tags.map((tag) => tag.id) || []
    init.claim_category = targetData?.claim_category || null
    init.note = targetData?.note || null
    init.a1_batch_id = targetData?.a1_batch_id || null
    init.extra_service = targetData?.extra_service?.code || null

    return init
  }, [data, dataCopy, initValueEdit])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit,
  })

  const handleAddNewItem = useCallback(
    async (name) => {
      const currentIndex = row.current[name]
      const nextIndex = currentIndex + 1

      let newItem
      switch (name) {
        case 'order_items':
          newItem = {
            [nextIndex]: {
              sku: '',
              title: '',
              quantity: null,
              unit_price: null,
            },
          }
          break
        case 'parcels':
          newItem = {
            [nextIndex]: {
              length: '',
              width: '',
              height: '',
              weight: '',
              dimension_unit: currentDimensionUnit || DIMENSION_UNIT.IN,
              weight_unit: currentWeightUnit || WEIGHT_UNIT.OZ,
              package_count: 1,
              package_type: null,
            },
          }
          break
        case 'custom_items':
          newItem = {
            [nextIndex]: {
              description: '',
              hs_tariff_number: '',
              country: '',
              quantity: '',
              value: '',
              weight: '',
            },
          }
          break
        default:
          return
      }

      const updatedItems = {...formik.values[name], ...newItem}

      // Set package_id of first parcel item to null when adding new parcel
      if (name === 'parcels') {
        const firstKey = Object.keys(updatedItems)?.[0] || '0'
        updatedItems[firstKey] = {
          ...updatedItems[firstKey],
          package_id: null,
          package_type: null,
          weight_limit: null,
        }
      }

      await formik.setFieldValue(name, updatedItems)

      const buildCustomSchema = () =>
        Yup.object()
          .default({})
          .shape(
            Object.entries(OrdersConfig.CUSTOMS.ITEMS).reduce((acc, [_, val]) => {
              acc[val.value] = validateCustoms(val.value)
              return acc
            }, {})
          )

      const validate = Object.entries(updatedItems).reduce((init, [key, _]) => {
        switch (name) {
          case 'order_items':
            init[key] = validateItems
            break
          case 'custom_items':
            init[key] = buildCustomSchema()
            break
          case 'parcels':
            init[key] = validateParcels
            break
          default:
            break
        }
        return init
      }, {})

      // Validate for the newItem
      switch (name) {
        case 'order_items':
          validate[nextIndex] = validateItems
          break
        case 'custom_items':
          validate[nextIndex] = buildCustomSchema()
          break
        case 'parcels':
          validate[nextIndex] = validateParcels
          break
        default:
          break
      }

      switch (name) {
        case 'custom_items':
          await setValidateSchema((prev) => ({
            ...prev,
            [name]: Yup.object().when('international', {
              is: (val) => val === true,
              then: Yup.object().default({}).shape(validate),
            }),
          }))
          break
        default:
          await setValidateSchema((prev) => ({
            ...prev,
            [name]: Yup.object().default({}).shape(validate),
          }))
          break
      }

      row.current[name] = nextIndex
    },
    [
      row,
      formik,
      currentDimensionUnit,
      currentWeightUnit,
      validateCustoms,
      validateItems,
      validateParcels,
      setValidateSchema,
    ]
  )

  const handleDeleteItem = useCallback(
    async (name, keyDelete) => {
      const updatedFormValues = Object.entries(formik.values[name]).reduce((acc, [key, value]) => {
        if (key !== keyDelete.toString()) {
          acc[key] = value
        }
        return acc
      }, {})

      const validate = Object.entries(updatedFormValues).reduce((init, [key, value]) => {
        switch (name) {
          case 'order_items':
            init[key] = validateItems
            break
          case 'custom_items':
            init[key] = Yup.object()
              .default({})
              .shape(
                Object.entries(OrdersConfig.CUSTOMS.ITEMS).reduce((initCustoms, [key, value]) => {
                  initCustoms[value.value] = validateCustoms(value.value)
                  return initCustoms
                }, {})
              )
            break
          case 'parcels':
            init[key] = validateParcels
            break
          default:
            init[key] = validateSchema[name].fields[key]
            break
        }
        return init
      }, {})

      switch (name) {
        case 'custom_items':
          await setValidateSchema((prev) => {
            return {
              ...prev,
              [name]: Yup.object().when('international', {
                is: (val) => val === true,
                then: Yup.object().shape({...validate}),
              }),
            }
          })
          break
        case 'parcels':
          await setValidateSchema((prev) => {
            return {
              ...prev,
              [name]: Yup.object()
                .default({})
                .shape({...validate}),
            }
          })
          break
        default:
          await setValidateSchema((prev) => {
            return {
              ...prev,
              [name]: Yup.object().shape(validate),
            }
          })
          break
      }
      await formik.setFieldValue(name, updatedFormValues)
    },
    [formik, validateItems, validateParcels, validateSchema, validateCustoms]
  )

  // BEGIN: Validate depends on service
  useEffect(() => {
    if (formik.values.international) {
      const customs = {}
      Object.keys(formik.values.custom_items).forEach((key) => {
        customs[key] = Yup.object()
          .default({})
          .shape(
            Object.entries(OrdersConfig.CUSTOMS.ITEMS).reduce((initCustoms, [key, value]) => {
              initCustoms[value.value] = validateCustoms(value.value)
              return initCustoms
            }, {})
          )
      })

      const custom_items = Yup.object().when('international', {
        is: (val) => val === true,
        then: Yup.object().default({}).shape(customs),
      })

      setValidateSchema((prev) => {
        return {
          ...prev,
          custom_items,
        }
      })

      let options = OPTIONS_CONTENT_TYPE

      if (FEDEX_INTERNATIONAL_SERVICES.includes(formik.values.service)) {
        options = OPTIONS_CONTENT_TYPE_FEDEX
      }

      if (formik.values.service) {
        setValidateSchema((prev) => {
          return {
            ...prev,
            international_option: Yup.object().shape({
              content: validateCustoms('content').oneOf(
                options.map((opt) => opt.value),
                intl.formatMessage({id: 'INCOMPATIBLE_CARRIER_OPTION'})
              ),
              description: validateCustoms('content_description'),
            }),
          }
        })
      }
    }

    if (formik.values.service === SERVICES.UPS_MI) {
      setValidateSchema((prev) => {
        return {
          ...prev,
          reference1: orderYup
            .reference('REF_1', true)
            .matches(
              REGEX_ALPHANUMERIC,
              intl.formatMessage(
                {id: 'INPUT_ALPHANUMERIC'},
                {input: intl.formatMessage({id: 'REF_1'})}
              )
            ),
        }
      })
    } else {
      setValidateSchema((prev) => {
        return {
          ...prev,
          reference1: orderYup.reference('REF_1'),
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.service])
  // END: Validate depends on service

  // BEGIN: remove validate international when change country
  useEffect(() => {
    if (!formik.values.international) {
      setValidateSchema((prev) => ({...prev, custom_items: initValidateSchema.custom_items}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.international])
  // END: remove validate international when change country

  useEffect(() => {
    const errorPaths = getErrorPaths(formik.errors)

    if (errorPaths.length > 0 && scrollToErrors) {
      const firstErrorPath = errorPaths.sort((a, b) => getKeyOrder(a) - getKeyOrder(b))[0]
      const input: any = document.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement

      if (input) {
        const elementToScroll = input.type === 'hidden' ? input.closest('div') : input

        if (elementToScroll) {
          setTimeout(() => {
            elementToScroll.scrollIntoView({behavior: 'smooth', block: 'center'})
            if (elementToScroll.type !== 'hidden') {
              elementToScroll.focus()
            }
          }, 0)
          setScrollToErrors(false)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToErrors, formik.errors])

  return {
    row,
    validateAddress,
    initValueEdit,
    validateSchema,
    validationSchema,
    data,
    setData,
    getInitValue,
    initialValues,
    formik,
    handleAddNewItem,
    handleDeleteItem,
    currentDimensionUnit,
    setCurrentDimensionUnit,
    currentWeightUnit,
    setCurrentWeightUnit,
    setScrollToErrors,
    isAccept,
    setIsAccept,
    isErrorUpicAccept,
    setIsErrorUpicAccept,
  }
}
